/**
 * V1.0.62: OfficeViewer - xem trực tiếp file Word (.docx) và Excel (.xlsx) trên trình duyệt
 *
 * - .docx: dùng docx-preview (render về HTML thuần)
 * - .xlsx/.xls: dùng SheetJS xlsx → render thành Table (mỗi sheet 1 tab)
 *
 * Lưu ý:
 * - File .doc (Word 97-2003) KHÔNG đọc được - phải tải về
 * - File .xls (Excel 97-2003) đọc được qua xlsx library
 * - Không cần convert server-side, mọi thứ chạy trên client
 */
import { useEffect, useRef, useState } from 'react';
import { Empty, Spin, Tabs, Table, Button, Tooltip, Modal, Input } from 'antd';
import { Download, FileText, Maximize2, Minimize2, Lock } from 'lucide-react';
import * as XLSX from 'xlsx';
import { renderAsync } from 'docx-preview';
import officecrypto from 'officecrypto-tool';

type FileKind = 'docx' | 'xlsx' | 'unknown';

interface Props {
  /** Hàm load file dưới dạng Blob (đã có auth) */
  loadBlob: () => Promise<Blob>;
  /** Tên file (để detect extension + cho phép tải xuống) */
  fileName: string;
  /** MIME type (optional, fallback nếu detect từ extension không ra) */
  fileMime?: string;
  /** Chiều cao container (default: '100%' để fill panel cha) */
  height?: string | number;
  /** Nút action thêm phía góc phải header (vd nút mở full screen) */
  extraHeaderRight?: React.ReactNode;
}

function detectKind(fileName: string, fileMime?: string): FileKind {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  if (ext === 'docx') return 'docx';
  if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm' || ext === 'csv') return 'xlsx';
  if (fileMime) {
    if (fileMime.includes('wordprocessingml')) return 'docx';
    if (fileMime.includes('spreadsheetml') || fileMime.includes('excel') || fileMime.includes('csv')) return 'xlsx';
  }
  return 'unknown';
}

export function OfficeViewer({ loadBlob, fileName, fileMime, height = '100%', extraHeaderRight }: Props) {
  const kind = detectKind(fileName, fileMime);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  // V1.0.63i: Password handling cho file Office encrypted
  const [rawBlob, setRawBlob] = useState<Blob | null>(null);  // blob gốc (có thể encrypted)
  const [needPassword, setNeedPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordWrong, setPasswordWrong] = useState(false);
  const [decrypting, setDecrypting] = useState(false);

  // Helper: detect file Office có encrypted không bằng magic bytes CFB
  // CFB header: D0 CF 11 E0 A1 B1 1A E1
  const detectEncrypted = async (b: Blob): Promise<boolean> => {
    try {
      const arr = new Uint8Array(await b.slice(0, 8).arrayBuffer());
      return arr[0] === 0xD0 && arr[1] === 0xCF && arr[2] === 0x11 && arr[3] === 0xE0
          && arr[4] === 0xA1 && arr[5] === 0xB1 && arr[6] === 0x1A && arr[7] === 0xE1;
    } catch {
      return false;
    }
  };

  // Helper: decrypt blob với password
  const decryptBlob = async (b: Blob, password: string): Promise<Blob> => {
    const arrayBuffer = await b.arrayBuffer();
    // officecrypto-tool accept ArrayBuffer trực tiếp (nó tự convert sang Buffer bên trong)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const decrypted: any = await officecrypto.decrypt(arrayBuffer as any, { password });
    console.log('[OfficeViewer] Decrypt success, output type:', typeof decrypted,
                'isBuffer:', decrypted?.constructor?.name,
                'length:', decrypted?.length ?? decrypted?.byteLength);
    // decrypted có thể là Buffer (Node) hoặc Uint8Array. Convert an toàn về Uint8Array
    let bytes: Uint8Array;
    if (decrypted instanceof Uint8Array) {
      bytes = decrypted;
    } else if (decrypted?.buffer instanceof ArrayBuffer) {
      bytes = new Uint8Array(decrypted.buffer, decrypted.byteOffset ?? 0, decrypted.length ?? decrypted.byteLength);
    } else {
      bytes = new Uint8Array(decrypted);
    }
    return new Blob([bytes as BlobPart], { type: b.type });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNeedPassword(false);
    setPasswordWrong(false);
    loadBlob()
      .then(async b => {
        if (cancelled) return;
        const isEnc = await detectEncrypted(b);
        if (isEnc) {
          // Lưu blob gốc, mở dialog nhập pass
          setRawBlob(b);
          setNeedPassword(true);
          setLoading(false);
        } else {
          setBlob(b);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message ?? 'Không tải được file');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [loadBlob]);

  const submitPassword = async () => {
    if (!rawBlob || !passwordInput.trim()) return;
    setDecrypting(true);
    setPasswordWrong(false);
    try {
      const decrypted = await decryptBlob(rawBlob, passwordInput);
      console.log('[OfficeViewer] Decrypted blob:', decrypted.size, 'bytes, type:', decrypted.type);
      setBlob(decrypted);
      setNeedPassword(false);
      setPasswordInput('');
    } catch (err: any) {
      console.error('[OfficeViewer] Decrypt error:', err);
      const msg = String(err?.message ?? err ?? '').toLowerCase();
      if (msg.includes('password') || msg.includes('incorrect') || msg.includes('invalid')) {
        // Pass sai
        setPasswordWrong(true);
      } else {
        // Lỗi khác (lib không support, format lạ...) — đóng dialog, hiện error
        setNeedPassword(false);
        setError(`Không giải mã được tài liệu: ${err?.message ?? 'lỗi không xác định'}`);
      }
    } finally {
      setDecrypting(false);
    }
  };

  const cancelPassword = () => {
    setNeedPassword(false);
    setError('Tài liệu được bảo vệ bằng mật khẩu. Bạn cần nhập đúng mật khẩu để xem.');
  };

  const handleDownload = async () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ============== UNKNOWN/UNSUPPORTED ==============
  if (kind === 'unknown') {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <FileText size={48} style={{ color: '#8a95a5', marginBottom: 12 }} />
        <div style={{ fontSize: 14, marginBottom: 8 }}>{fileName}</div>
        <div style={{ fontSize: 12, color: '#8a95a5', marginBottom: 16 }}>
          Loại file không hỗ trợ xem trước. Hãy tải về để mở.
        </div>
        <Button type="primary" icon={<Download size={14} />} onClick={handleDownload}>
          Tải về
        </Button>
      </div>
    );
  }

  // ============== LOADING ==============
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin tip="Đang tải file..." />
      </div>
    );
  }

  // V1.0.63i: File Office encrypted → Modal nhập password
  if (needPassword) {
    return (
      <>
        <Empty
          image={<Lock size={48} style={{ color: '#fa8c16' }} />}
          description={
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                Tài liệu được bảo vệ bằng mật khẩu
              </div>
              <div style={{ fontSize: 12, color: '#8a95a5' }}>
                Vui lòng nhập mật khẩu để xem nội dung
              </div>
            </div>
          }
          style={{ padding: 60 }}
        />
        <Modal
          open
          title={<span><Lock size={14} style={{ verticalAlign: -2, marginRight: 8, color: '#fa8c16' }} />Tài liệu được bảo vệ</span>}
          onCancel={cancelPassword}
          onOk={submitPassword}
          okText="Mở tài liệu"
          cancelText="Hủy"
          confirmLoading={decrypting}
          maskClosable={false}
          width={440}
        >
          <div style={{ fontSize: 13, color: '#5b6875', marginBottom: 12 }}>
            {passwordWrong
              ? <span style={{ color: '#cf1322' }}>⚠ Mật khẩu không đúng. Vui lòng nhập lại.</span>
              : `File ${fileName} được bảo vệ bằng mật khẩu. Vui lòng nhập mật khẩu để xem.`}
          </div>
          <Input.Password
            autoFocus
            placeholder="Nhập mật khẩu..."
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onPressEnter={submitPassword}
            disabled={decrypting}
          />
        </Modal>
      </>
    );
  }

  if (error || !blob) {
    return <Empty description={error ?? 'Không tải được file'} />;
  }

  // ============== HEADER ==============
  const containerStyle: React.CSSProperties = fullscreen
    ? {
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#fff', display: 'flex', flexDirection: 'column',
    }
    : { display: 'flex', flexDirection: 'column', height };

  const header = (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 12px', borderBottom: '1px solid #e8edf2',
      background: '#f7f9fb', flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
        <FileText size={16} style={{ color: '#1976D2', flexShrink: 0 }} />
        <span style={{
          fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{fileName}</span>
        <span style={{
          fontSize: 10, padding: '1px 6px', borderRadius: 3,
          background: kind === 'docx' ? '#e6f4ff' : '#e6fffb',
          color: kind === 'docx' ? '#1976D2' : '#08979c',
          fontWeight: 600, flexShrink: 0,
        }}>
          {kind === 'docx' ? 'WORD' : 'EXCEL'}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        {extraHeaderRight}
        <Tooltip title="Tải về">
          <Button size="small" icon={<Download size={14} />} onClick={handleDownload} />
        </Tooltip>
        <Tooltip title={fullscreen ? 'Thu nhỏ' : 'Phóng to'}>
          <Button size="small"
            icon={fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            onClick={() => setFullscreen(f => !f)}
          />
        </Tooltip>
      </div>
    </div>
  );

  return (
    <div style={containerStyle}>
      {header}
      <div style={{ flex: 1, overflow: 'auto', background: '#f0f2f5' }}>
        {kind === 'docx'
          ? <DocxRender blob={blob} />
          : <XlsxRender blob={blob} />
        }
      </div>
    </div>
  );
}

// ============================================================================
// DOCX RENDER
// ============================================================================
function DocxRender({ blob }: { blob: Blob }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    if (!containerRef.current) return;
    setRendering(true);
    setError(null);
    // Clear nội dung cũ
    containerRef.current.innerHTML = '';
    renderAsync(blob, containerRef.current, undefined, {
      className: 'docx-content',
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      ignoreFonts: false,
      breakPages: true,
      ignoreLastRenderedPageBreak: true,
      experimental: false,
      trimXmlDeclaration: true,
      useBase64URL: false,
      renderHeaders: true,
      renderFooters: true,
      renderFootnotes: true,
      renderEndnotes: true,
    })
      .catch((err: any) => {
        setError(err?.message ?? 'Không render được file Word');
      })
      .finally(() => setRendering(false));
  }, [blob]);

  return (
    <div style={{ padding: 16 }}>
      {rendering && (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin tip="Đang render Word..." />
        </div>
      )}
      {error && <Empty description={`Lỗi: ${error}`} />}
      <div
        ref={containerRef}
        style={{
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: 4,
          margin: '0 auto',
          maxWidth: 900,
        }}
      />
    </div>
  );
}

// ============================================================================
// XLSX RENDER
// ============================================================================
interface SheetData {
  name: string;
  // V1.0.64: HTML render (giữ merges + layout giống Excel gốc)
  html: string;
  // Sample data dùng cho preview text-only (fallback)
  rowCount: number;
}

function XlsxRender({ blob }: { blob: Blob }) {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(true);

  useEffect(() => {
    setParsing(true);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array', cellDates: true, cellStyles: true });
        const parsed: SheetData[] = wb.SheetNames.map(name => {
          const sheet = wb.Sheets[name];
          // V1.0.64: Render thành HTML để giữ merges + styling + layout
          //   `sheet_to_html` tự xử lý colspan/rowspan/inline-style
          let html = XLSX.utils.sheet_to_html(sheet, {
            id: `sheet-${name}`,
            editable: false,
          });
          // V1.0.64-fix: SheetJS không inject column widths vào HTML mặc định.
          // Tự thêm <colgroup> từ sheet['!cols'] (Excel column widths).
          // !cols[i].wpx = pixel width hoặc !cols[i].wch = character width
          const cols = (sheet as any)['!cols'] as Array<{ wpx?: number; wch?: number; hidden?: boolean }> | undefined;
          const ref = sheet['!ref'] ?? 'A1:A1';
          const range = XLSX.utils.decode_range(ref);
          const numCols = range.e.c - range.s.c + 1;
          if (numCols > 0) {
            const colTags: string[] = [];
            for (let i = 0; i < numCols; i++) {
              const c = cols?.[i];
              let widthPx: number;
              if (c?.hidden) {
                widthPx = 0;
              } else if (c?.wpx) {
                widthPx = c.wpx;
              } else if (c?.wch) {
                // Excel char width ~ 7px (font 11pt default)
                widthPx = Math.round(c.wch * 7 + 5);
              } else {
                // Default Excel column width = 64px
                widthPx = 64;
              }
              colTags.push(`<col style="width:${widthPx}px">`);
            }
            const colgroup = `<colgroup>${colTags.join('')}</colgroup>`;
            // Inject colgroup ngay sau <table ...>
            html = html.replace(/(<table[^>]*>)/i, `$1${colgroup}`);
          }
          const rowCount = range.e.r - range.s.r + 1;
          return { name, html, rowCount };
        });
        setSheets(parsed);
      } catch (err: any) {
        setError(err?.message ?? 'Không đọc được file Excel');
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setError('Lỗi đọc file');
      setParsing(false);
    };
    reader.readAsArrayBuffer(blob);
  }, [blob]);

  if (parsing) return <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="Đang đọc Excel..." /></div>;
  if (error) return <Empty description={`Lỗi: ${error}`} />;
  if (sheets.length === 0) return <Empty description="File Excel rỗng" />;

  // Nếu chỉ 1 sheet, không cần tab — render trực tiếp
  if (sheets.length === 1) {
    return (
      <div style={{ padding: 12, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <SheetHtmlView sheet={sheets[0]} />
      </div>
    );
  }

  return (
    <div style={{
      padding: 12, height: '100%', display: 'flex', flexDirection: 'column',
      minHeight: 0,
    }}>
      <Tabs
        items={sheets.map((s, idx) => ({
          key: String(idx),
          label: s.name,
          children: <SheetHtmlView sheet={s} />,
        }))}
        size="small"
        // V1.0.64: Tabs fill parent + tab content cũng fill (cần CSS class custom)
        className="hv-xlsx-tabs-fill"
        style={{ flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

function SheetHtmlView({ sheet }: { sheet: SheetData }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {sheet.rowCount > 500 && (
        <div style={{
          fontSize: 11, color: '#fa8c16', marginBottom: 8,
          padding: '6px 10px', background: '#fff7e6', borderRadius: 4,
          flexShrink: 0,
        }}>
          ⚠ Sheet có {sheet.rowCount} dòng — có thể tải chậm. Cân nhắc tải file về để xem mượt hơn.
        </div>
      )}
      <div
        className="hv-xlsx-html-render"
        // V1.0.64: Render HTML từ SheetJS - giữ merges, col widths, basic styling từ file Excel gốc
        dangerouslySetInnerHTML={{ __html: sheet.html }}
        style={{
          flex: 1,
          overflow: 'auto',
          background: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          padding: 8,
          minHeight: 0,
        }}
      />
    </div>
  );
}

export default OfficeViewer;
